import { useState } from "react";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useDebouncedValue } from "@circle-react/hooks/utils/useDebouncedValue";
import { useCommunityMemberPickerContext } from "../CommunityMemberPickerContext";
import { SearchResults } from "../SearchResults";
import type { RecordType } from "../types";
import { useCommunityMemberSearch, useGeneralSearch } from "./hooks";

const QUERY_DEBOUNCE_MILLISECONDS = 300;

interface SearchProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  disableSearch?: boolean;
  placeholder?: string;
  selectionModalRef?: React.RefObject<HTMLElement>;
}

export const Search = ({
  placeholder = "",
  className = "",
  selectionModalRef,
  disableSearch = false,
  ...rest
}: SearchProps) => {
  const {
    onAdd,
    excludeCommunityMemberIds = [],
    includeAllResultTypes: shouldIncludeAllResultTypes,
  } = useCommunityMemberPickerContext();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, QUERY_DEBOUNCE_MILLISECONDS);
  const emptyOrDebouncedQuery = !query ? query : debouncedQuery;
  const { records: communityMemberRecords } = useCommunityMemberSearch(
    emptyOrDebouncedQuery,
    {
      excludeCommunityMemberIds,
      enabled: !shouldIncludeAllResultTypes,
    },
  );
  const { records: generalRecords } = useGeneralSearch(emptyOrDebouncedQuery, {
    enabled: shouldIncludeAllResultTypes,
  });
  const records = shouldIncludeAllResultTypes
    ? generalRecords
    : communityMemberRecords;
  const onSelect = (record: RecordType) => {
    onAdd(record);
    setQuery("");
  };
  const handleReset = () => setQuery("");

  return (
    <div
      className={classNames("relative overflow-visible", className)}
      {...rest}
    >
      <input
        className="border-primary bg-primary text-dark placeholder:text-light w-full rounded-lg border px-4 py-3 placeholder:opacity-100"
        type="text"
        placeholder={placeholder}
        autoFocus
        value={query}
        onChange={e => setQuery(e.target.value)}
        disabled={disableSearch}
      />
      <SearchResults
        records={records}
        onSelect={onSelect}
        onReset={handleReset}
        selectionModalRef={selectionModalRef}
        includeAllResultTypes={shouldIncludeAllResultTypes}
      />
    </div>
  );
};
